/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphLocale } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";

import {
	createSectionModelLookup,
	getPageSectionsData,
	getPageSectionsDetails,
	mapArticleSectionsToSections,
	mapHeroToSection,
	mapInlineSectionsToSections,
	mapRefSectionsToSections,
	HygraphPageSectionsApiConfig,
} from "./pageSections";

export interface PageSectionsRouteDeps {
	/** Site identifier echoed back to the consumer (e.g. `twinmotion`). */
	app: string;
	/** Site's Hygraph locale codes, used to map the request locale to a Hygraph locale. */
	hygraphLocales: string[];
	/** Static Studio config for deep-link construction. */
	hygraph: HygraphPageSectionsApiConfig;
	/** Deployment environment (the caller's `EPIC_ENV`). This internal tooling route is disabled
	 *  in production; the caller passes it so environment coupling stays out of the library. */
	epicEnv?: string;
}

const jsonResponse = (status: number, body: unknown, headers?: Record<string, string>): Response =>
	new Response(JSON.stringify(body), {
		status,
		headers: { "content-type": "application/json", ...headers },
	});

/**
 * Handle a page-sections request. Reads `locale` and `slug` from the query string and
 * returns the section layout for the matching published page (see API-SPEC-page-sections.md).
 * The `app` and Hygraph Studio config are injected by the caller (resolved from the request's
 * site), keeping this handler free of website-specific config.
 */
export const pageSectionsRoute = async (req: Request, deps: PageSectionsRouteDeps): Promise<Response> => {
	// Internal tooling API — enabled only on non-production (gamedev) environments, and only
	// where the Hygraph endpoint is configured (the response's deep links are useless
	// without it). In production, or when unconfigured, it behaves as if the route does not exist.
	const isProduction = deps?.epicEnv?.toLowerCase().trim() === "prod";
	const isHygraphMissing =
		!deps?.hygraph || !deps.hygraph.studioBaseUrl || !deps.hygraph.projectId || !deps.hygraph.environmentId;
	if (isProduction || isHygraphMissing) {
		return jsonResponse(404, { error: "NOT_FOUND", message: "Not found" });
	}

	try {
		const { searchParams } = new URL(req.url);
		const locale = searchParams.get("locale");
		const slug = searchParams.get("slug");

		if (!locale) {
			return jsonResponse(400, { error: "INVALID_PARAMETERS", message: "Missing required parameter: locale" });
		}
		if (!slug) {
			return jsonResponse(400, { error: "INVALID_PARAMETERS", message: "Missing required parameter: slug" });
		}

		const hygraphLocale = getHygraphLocale(deps.hygraphLocales, locale);
		const page = await getPageSectionsData(slug, hygraphLocale);

		if (!page) {
			return jsonResponse(404, {
				error: "PAGE_NOT_FOUND",
				message: `No published page found for app='${deps.app}', locale='${locale}', slug='${slug}'`,
			});
		}

		// Resolve display names and model ids from this project's generated model-id lookups, keyed
		// by the site's Hygraph project id.
		const lookup = createSectionModelLookup(deps.hygraph.projectId);
		const projectModelIds = lookup.projectModelIds;

		// Reference sections arrive as entity stubs; resolve each one's config/variant detail.
		const refSections = await getPageSectionsDetails(page.refSectionEntities, hygraphLocale);

		const pageModelId = lookup.modelId(page.typename);
		const pageEntryId = page.id;

		// Base URL for a Hygraph Studio content entry deep link.
		const studioBaseUrl = "https://app.hygraph.com/";
		const contentBaseUrl = `${studioBaseUrl}${deps.hygraph.projectId}/${deps.hygraph.environmentId}/content`;
		const getEntryUrl = (modelId: string | null | undefined, entryId: string | null | undefined): string =>
			`${contentBaseUrl}/${modelId}/entry/${entryId}`;

		// Sections without their own modelId fall back to the page's model/entry.
		const pageEntryUrl = getEntryUrl(pageModelId, pageEntryId);

		// Order mirrors how the page renders: the hero (referenced directly on the page) first, then the
		// article body (blog/plugin `articleComponents`), then the resolved reference sections, then any
		// inline union sections. Reindex so the combined list stays sequential from 0.
		const heroSection = mapHeroToSection(page.hero, lookup);
		const sections = [
			...(heroSection ? [heroSection] : []),
			...mapArticleSectionsToSections(page.articleSections, lookup),
			...mapRefSectionsToSections(refSections, lookup),
			...mapInlineSectionsToSections(page.inlineSections, lookup),
		].map((section, index) => ({
			...section,
			index,
			entryUrl: section.hygraphModelId ? getEntryUrl(section.hygraphModelId, section.hygraphEntryId) : pageEntryUrl,
		}));

		const body = {
			info: {
				alias: projectModelIds?.alias,
				name: projectModelIds?.name,
				lookupDisplayName: projectModelIds?.lookupDisplayName,
				lookupModelId: projectModelIds?.lookupModelId,
				url: studioBaseUrl,
				projectId: deps.hygraph.projectId,
				environmentId: deps.hygraph.environmentId,
			},
			page: {
				slug,
				locale,
				app: deps.app,
				templateName: page.typename,
				hygraphEntryId: pageEntryId,
				hygraphModelId: pageModelId,
				displayName: lookup.displayName(page.typename),
				entryUrl: pageEntryUrl,
			},
			sections,
		};

		// Mirror content caching: short public TTL, consistent with page revalidation windows.
		return jsonResponse(200, body, { "Cache-Control": "public, max-age=60, stale-while-revalidate=300" });
	} catch (e) {
		console.error("[Page Sections] pageSectionsRoute() failed", { error: isError(e) ? e.message : String(e) });
		return jsonResponse(500, { error: "INTERNAL_ERROR", message: "Failed to fetch page data" });
	}
};
